import React, { useEffect, useState, useRef } from "react";
import { Quote as QuoteIcon, ArrowUpRight, MessageSquare, Download, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useNotification } from "../context/NotificationContext";
import { UserGalleryService } from "../services/UserGalleryServices";
import { UserPressService } from "../services/UserPressServices";
import { UserBiographyServices } from "../services/BiographyServices";

export default function Press({ quotes = [], coverage = [], loading = false }) {
  const { showNotification } = useNotification();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [epk, setEpk] = useState(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
          setIsVisible(true);
          observer.unobserve(section);
        }
      },
      {
        threshold: [0, 0.1, 0.2],
        rootMargin: "50px"
      },
    );

    observer.observe(section);

    // Immediate check
    const rect = section.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setIsVisible(true);
    }

    return () => {
      if (section) observer.unobserve(section);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const fetchEPK = async () => {
      try {
        const response = await UserPressService.getEPK();
        if (response.status === "success") {
          setEpk(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch EPK:", err);
      }
    };
    fetchEPK();
  }, []);

  const handleDownloadEPK = () => {
    if (epk && epk.file_path) {
      window.open(epk.file_path, "_blank");
      showNotification({ message: "EPK download started", type: "success" });
    } else {
      showNotification({ message: "EPK is not available at the moment.", type: "info" });
    }
  };

  const handleDownloadBio = async () => {
    try {
      await UserBiographyServices.downloadBio();
      showNotification({ message: "Bio download started", type: "success" });
    } catch (err) {
      showNotification({ message: "Bio download failed. Please try again later.", type: "error" });
    }
  };

  const [downloading, setDownloading] = useState(false);

  const handleDownloadPhotos = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await UserGalleryService.downloadPhotos();
      showNotification({ message: "Download started successfully", type: "success" });
    } catch (err) {
      console.error("Download failed:", err);

      let errorMessage = "Failed to download press photos. Please try again later.";
      if (err.response && err.response.data) {
        if (err.response.data instanceof Blob) {
          const text = await err.response.data.text();
          try {
            const jsonData = JSON.parse(text);
            errorMessage = jsonData.message || errorMessage;
          } catch (e) { }
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }

      showNotification({
        message: errorMessage,
        type: err.response?.status === 404 ? "info" : "error"
      });
    } finally {
      setDownloading(false);
    }
  };

  const SkeletonQuote = () => (
    <div className="bg-white p-6 sm:p-8 lg:p-10 border border-black/10 animate-pulse shadow-sm">
      <QuoteIcon size={32} className="text-gray-200 mb-6" />
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 w-full rounded" />
        <div className="h-4 bg-gray-200 w-5/6 rounded" />
        <div className="h-4 bg-gray-200 w-4/6 rounded" />
      </div>
      <div className="h-3 bg-gray-200 w-24 rounded mt-8" />
    </div>
  );

  const SkeletonPressItem = () => (
    <div className="p-4 sm:p-6 border-b border-black/10 flex justify-between items-center animate-pulse">
      <div className="space-y-2 w-2/3">
        <div className="h-4 bg-gray-200 w-full rounded" />
        <div className="h-3 bg-gray-200 w-1/2 rounded" />
      </div>
      <div className="w-12 h-6 bg-gray-200 rounded" />
    </div>
  );

  return (
    <section
      ref={sectionRef}
      id="press"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#fcfcfc] overflow-hidden"
      style={{
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-24">
          {/* Quotes side */}
          <div
            className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
              }`}
          >
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-normal text-black mb-8 sm:mb-12"
              style={{
                fontFamily: "Playfair Display, Georgia, serif",
                fontWeight: 300,
                letterSpacing: "-0.01em",
              }}
            >
              Voices
            </h2>

            <div className="min-h-[400px]">
              {loading ? (
                <div className="space-y-6 sm:space-y-8">
                  {[1, 2].map((i) => (
                    <SkeletonQuote key={i} />
                  ))}
                </div>
              ) : quotes.length > 0 ? (
                <div className="space-y-6 sm:space-y-8">
                  {quotes.map((quote, index) => (
                    <div
                      key={quote.id || index}
                      className="bg-white p-6 sm:p-8 lg:p-10 border border-black/5 shadow-[0_4px_20px_rgba(0,0,0,0.03)] relative overflow-hidden group hover:shadow-[0_10px_40px_rgba(0,0,0,0.08)] transition-all duration-500"
                    >
                      <QuoteIcon
                        size={40}
                        className="text-black/5 absolute -top-2 -left-2 transform -rotate-12 transition-transform duration-500 group-hover:rotate-0"
                      />
                      <blockquote
                        className="text-lg sm:text-xl lg:text-2xl text-black leading-relaxed mb-6 sm:mb-8"
                        style={{
                          fontFamily: "Crimson Pro, Georgia, serif",
                          fontStyle: "italic",
                        }}
                      >
                        "{quote.text}"
                      </blockquote>
                      <cite
                        className="text-xs sm:text-sm font-bold tracking-[0.2em] text-black uppercase not-italic block"
                        style={{ letterSpacing: "2px" }}
                      >
                        — {quote.source}
                      </cite>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-black/30 rounded-xl bg-white">
                  <MessageSquare size={48} className="mx-auto text-black/40 mb-4" />
                  <p className="text-black font-serif italic text-sm">Critical acclaim incoming.</p>
                </div>
              )}
            </div>
          </div>

          {/* Press coverage side */}
          <div
            className={`transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
          >
            <div className="flex items-center justify-between mb-8 sm:mb-12">
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-normal text-black"
                style={{
                  fontFamily: "Playfair Display, Georgia, serif",
                  fontWeight: 300,
                  letterSpacing: "-0.01em",
                }}
              >
                Press
              </h2>
              <Link
                to="/press"
                className="text-xs sm:text-sm font-bold tracking-widest text-black/80 hover:text-black transition-colors duration-200 border-b border-black/40 hover:border-black uppercase pb-1"
                style={{ letterSpacing: "1px" }}
              >
                Full Archive
              </Link>
            </div>

            <div className="min-h-[400px]">
              {loading ? (
                <div className="divide-y divide-black/5">
                  {[1, 2, 3, 4].map((i) => (
                    <SkeletonPressItem key={i} />
                  ))}
                </div>
              ) : coverage.length > 0 ? (
                <div className="divide-y divide-black/5">
                  {coverage.map((item, index) => (
                    <article
                      key={item.id || index}
                      className="group py-6 sm:py-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6 hover:translate-x-2 transition-transform duration-300"
                    >
                      <div className="space-y-2">
                        <div className="text-[10px] font-bold tracking-[0.2em] text-black uppercase">
                          {item.publication} • {item.date_display}
                        </div>
                        <h3
                          className="text-lg sm:text-xl font-medium text-black group-hover:text-black/60 transition-colors duration-200"
                          style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                          {item.headline}
                        </h3>
                        <p
                          className="text-sm text-black font-medium line-clamp-2 max-w-[400px]"
                          style={{ fontFamily: "Crimson Pro, Georgia, serif" }}
                        >
                          {item.excerpt}
                        </p>
                      </div>
                      <a
                        href={item.link || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-black/10 flex items-center justify-center group-hover:bg-black group-hover:text-white group-hover:border-black transition-all duration-300 transform group-hover:rotate-45 flex-shrink-0 touch-manipulation self-end sm:self-center"
                      >
                        <ArrowUpRight size={20} />
                      </a>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-black/20 rounded-xl bg-gray-50">
                  <p className="text-black font-serif italic text-sm">Media coverage coming soon.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions / Downloads */}
        <div
          className={`mt-24 pt-16 border-t border-black/5 transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <button
              onClick={handleDownloadEPK}
              className="group bg-white p-8 border border-black/5 hover:border-black/20 hover:shadow-xl transition-all duration-500 flex items-center justify-between"
            >
              <div className="text-left">
                <p className="text-[10px] font-bold tracking-[0.2em] text-black/40 uppercase mb-1">Electronic Press Kit</p>
                <h4 className="text-xl font-medium text-black" style={{ fontFamily: "Playfair Display, serif" }}>DOWNLOAD EPK</h4>
              </div>
              <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center transform group-hover:scale-110 transition-transform duration-500">
                <Download size={20} />
              </div>
            </button>

            <button
              onClick={handleDownloadBio}
              className="group bg-white p-8 border border-black/5 hover:border-black/20 hover:shadow-xl transition-all duration-500 flex items-center justify-between"
            >
              <div className="text-left">
                <p className="text-[10px] font-bold tracking-[0.2em] text-black/40 uppercase mb-1">Official Biography</p>
                <h4 className="text-xl font-medium text-black" style={{ fontFamily: "Playfair Display, serif" }}>DOWNLOAD BIO</h4>
              </div>
              <div className="w-12 h-12 rounded-full border border-black/10 flex items-center justify-center group-hover:bg-black group-hover:text-white group-hover:border-black transition-all duration-500">
                <Download size={20} />
              </div>
            </button>

            <button
              onClick={handleDownloadPhotos}
              disabled={downloading}
              className="group bg-white p-8 border border-black/5 hover:border-black/20 hover:shadow-xl transition-all duration-500 flex items-center justify-between disabled:opacity-50"
            >
              <div className="text-left">
                <p className="text-[10px] font-bold tracking-[0.2em] text-black/40 uppercase mb-1">Press Photography</p>
                <h4 className="text-xl font-medium text-black" style={{ fontFamily: "Playfair Display, serif" }}>MEDIA ASSETS</h4>
              </div>
              <div className="w-12 h-12 rounded-full border border-black/10 flex items-center justify-center group-hover:bg-black group-hover:text-white group-hover:border-black transition-all duration-500">
                {downloading ? <Loader2 size={20} className="animate-spin" /> : <Download size={20} />}
              </div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
